import {AttrsSchema, Component} from 'panel';
import mapValues from 'lodash/mapValues';
import findKey from 'lodash/findKey';

import {
  ChartType,
  ChartTypeSelection,
  ValueRepresentationType,
  ValueRepresentationTypeSelection,
} from 'common/report/constants';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {OrderedTimeUnit, TimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {headerDateSelectorRenderer} from 'common/widgets/date-range-preset-bar/header-date-selector';
import {currentProjectStore} from 'common/report/stores/project-details';

import {template} from './template';
import css from './index.styl?inline';
import {CAPTURED_TRACKING, TrackingEvent} from './tracking';
import {ChartHeaderAttrs, ChartHeaderSelection} from './types';

/* eslint-disable @typescript-eslint/naming-convention */
enum AttributeFields {
  dateRange = `date-range`,
  comparisonDateRange = `comparison-date-range`,
  chartType = `selected-chart-type`,
  dateUnit = `selected-date-unit`,
  valueRepresentation = `selected-value-representation`,
}
/* eslint-enable */

export class ChartHeader extends Component<EmptyObj, ChartHeaderAttrs> {
  static get attrsSchema(): AttrsSchema<ChartHeaderAttrs> {
    return {
      ...super.attrsSchema,
      'max-data-history-days': `number`,
      'date-range': `json`,
      'comparison-date-range': `json`,
      'chart-types': {
        type: `json`,
        default: [],
      },
      'chart-type-overrides': {
        type: `json`,
        default: {},
      },
      'date-units': {
        type: `json`,
        default: [],
      },
      'disabled-value-representation': {
        type: `string`,
        enum: Object.values(ValueRepresentationType),
      },
      'value-representations': {
        type: `json`,
        default: [],
      },
      'date-unit-overrides': {
        type: `json`,
        default: {},
      },
      'selected-chart-type': {
        type: `string`,
        enum: Object.values(ChartType),
      },
      'selected-date-unit': {
        type: `string`,
        enum: OrderedTimeUnit,
      },
      'selected-value-representation': {
        type: `string`,
        enum: Object.values(ValueRepresentationType),
        default: ValueRepresentationType.Absolute,
      },
      'context-panel-tooltip-data': `json`,
      'show-date-range-selector': `boolean`,
      'show-time-comparison-menu': `boolean`,
      'time-comparison-disabled-reason': `string`,
      'date-range-tooltip-data': `json`,
      'time-comparison-tooltip-data': `json`,
      'track-name': `string`,
      'project-id': `number`,
      'workspace-id': `number`,
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
    };
  }

  private getSelectionObjectFromAttrs(): ChartHeaderSelection {
    return mapValues(AttributeFields, (attribute) => this.attr(attribute));
  }

  public get config() {
    return {
      css,
      template,
      useShadowDom: true,
      helpers: {
        DisplayContext,

        dateRangeSelectorRenderer: () => {
          return headerDateSelectorRenderer({
            displayContext: DisplayContext.FullLength,
            selectedDateRange: this.attr(`date-range`),
            projectAsOfDate: this.attr(`project-as-of-date`),
            maxHistoryDays: this.attr(`max-data-history-days`),
            currentProjectStore,
            projectId: this.attr(`project-id`),
            tooltipData: this.attr(`date-range-tooltip-data`),
            handleTrack: this.helpers.captureTracking,
            handleApply: (ev) => this.emitUpdateEvent({dateRange: ev.detail.dateRange}),
          });
        },

        updateChartType: (ev) => {
          const chartType = ev.detail.item.value as ChartType;
          this.track(TrackingEvent.SelectChart, {
            'Chart type': chartType,
          });

          this.emitUpdateEvent({chartType});
        },
        updateValueRepresentation: (ev) => {
          const valueRepresentation = ev.detail.item.value as ValueRepresentationType;
          this.track(TrackingEvent.SelectValueRepresentation, {
            Representatipn: valueRepresentation,
          });

          this.emitUpdateEvent({valueRepresentation});
        },
        updateDateUnit: (ev) => {
          const dateUnit = ev.detail.item.value as TimeUnit;
          this.track(TrackingEvent.SelectDateUnit, {
            Unit: dateUnit,
          });

          this.emitUpdateEvent({dateUnit});
        },
        preventTracking: (ev) => ev.stopPropagation(),
        captureTracking: (ev) => {
          const {eventPrefix, props} = ev.detail;
          const eventName = ev.detail.eventName.replace(eventPrefix, ``);

          if (this.isCapturedTrackEvent(eventName)) {
            this.helpers.preventTracking(ev);
            this.track(eventName, props);
          }
        },

        hasSlot: (slotName: string): boolean => {
          return !!this.querySelector(`[slot="${slotName}"]`);
        },

        chartSelectorAttributes: () => {
          return {
            sections: JSON.stringify([
              {
                items: this.attr(`chart-types`)
                  .map(
                    (type) =>
                      ChartTypeSelection[type] && {
                        ...ChartTypeSelection[type],
                        isSelected: ChartTypeSelection[type].value === this.attr(`selected-chart-type`),
                        ...(this.attr(`chart-type-overrides`)[type] || {}),
                      },
                  )
                  .filter(Boolean),
              },
            ]),
            theme: `secondary`,
            icon: JSON.stringify({leftIcon: ChartTypeSelection[this.attr(`selected-chart-type`)].icon}),
            'menu-width': 200,
            elref: `chartTypeSelector`,
          };
        },

        dateUnitSelectorAttributes: () => {
          return {
            sections: JSON.stringify([
              {
                items: this.attr(`date-units`)
                  .map((value) => ({
                    value,
                    label: TimeUnitLabels[value],
                    isSelected: value === this.attr(`selected-date-unit`),
                    ...(this.attr(`date-unit-overrides`)[value] || {}),
                  }))
                  .filter(Boolean),
              },
            ]),
            theme: `secondary`,
            'menu-width': 120,
            elref: `dateUnitSelector`,
          };
        },

        valueRepresentationSelectorAttributes: () => {
          return {
            sections: JSON.stringify([
              {
                items: this.attr(`value-representations`)
                  .map(
                    (type) =>
                      ValueRepresentationTypeSelection[type] && {
                        ...ValueRepresentationTypeSelection[type],
                        isSelected:
                          ValueRepresentationTypeSelection[type].value === this.attr(`selected-value-representation`),
                        isDisabled:
                          ValueRepresentationTypeSelection[type].value === this.attr(`disabled-value-representation`),
                      },
                  )
                  .filter(Boolean),
              },
            ]),
            theme: `secondary`,
            icon: JSON.stringify({
              leftIcon: ValueRepresentationTypeSelection[this.attr(`selected-value-representation`)].icon,
            }),
            tooltip: JSON.stringify({
              text: this.helpers.getSelectedValueRepresentation().label,
              placement: `top`,
            }),
            'icon-only': true,
            'show-icon-only-down-caret': true,
            'menu-width': 160,
            elref: `valueRepresentationSelector`,
          };
        },
        getSelectedValueRepresentation: () =>
          ValueRepresentationTypeSelection[
            this.attr(`value-representations`).find(
              (type) => ValueRepresentationTypeSelection[type].value === this.attr(`selected-value-representation`),
            )
          ],
        updateTimeComparison: (ev) => this.emitUpdateEvent({comparisonDateRange: ev.detail}),
        removeComparisonDateRange: () => {
          this.emitUpdateEvent({comparisonDateRange: null});
          this.track(TrackingEvent.RemoveComparisonDateRange);
        },

        contextPanelTransform: (x) => {
          return this.attr(`context-panel-tooltip-data`) && this.attr(`context-panel-tooltip-data`)[x.value];
        },

        updateAfterStoreValueChange: () => this.update(),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    currentProjectStore.subscribe(this.helpers.updateAfterStoreValueChange);
  }

  disconnectedCallback() {
    currentProjectStore.unsubscribe(this.helpers.updateAfterStoreValueChange);
    super.disconnectedCallback();
  }

  private track(event: string, props: Obj = {}) {
    if (!this.attr(`track-name`)) {
      return;
    }

    if (this.isCapturedTrackEvent(event)) {
      emitTrackEvent(this, {
        eventName: event,
        // TODO: [shawn] - Tracking under mp-query-builder for now until we redo eventPrefixes
        eventPrefix: `mp-query-builder`,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          source: `chart-header`,
        },
        cancelable: true,
      });
    } else {
      throw new Error(`tracking event not defined: ${event}`);
    }
  }

  private isCapturedTrackEvent(event: string) {
    return findKey(TrackingEvent, (eventName) => eventName === event) || CAPTURED_TRACKING.has(event);
  }

  private emitUpdateEvent(updates: Partial<ChartHeaderSelection>) {
    const existingSelection = this.getSelectionObjectFromAttrs();
    const selection = {...existingSelection, ...updates};

    const detail = {
      ...selection,
      delta: updates,
    };

    this.dispatchEvent(new CustomEvent(`update`, {detail}));
  }
}

export const ChartHeaderEl = defineAndMakeJsxFactory(`mp-chart-header`, ChartHeader);
